import { useEffect, useState } from "react";
import ComponentCard from "../../../components/common/ComponentCard";
import Button from "../../../components/ui/button/Button";
import Label from "../../../components/form/Label";
import Input from "../../../components/form/input/InputField";
import Form from "../../../components/form/Form";
import { useLocation, useNavigate } from "react-router";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/light.css";
import { CalenderIcon } from "../../../icons";
import { Table, TableBody, TableCell, TableHeader, TableRow } from "../../../components/ui/table";
import { useMemo } from "react";
import InspectionItemOutcome from "./InspectionItemOutcome";
import AddInspectionItemModal from "./AddInspectionItemModal";
import { jwtDecode } from "jwt-decode";

const apiUrl = process.env.REACT_APP_API_URL;

interface UpdateInspectionHistoryProps {
  inspectionHoistory?: any;
}

export default function UpdateInspectionHistory({ inspectionHoistory }: UpdateInspectionHistoryProps) {
  const [inspectionDate, setInspectionDate] = useState("");
  const [remarksInput, setRemarksInput] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const inspectionData = inspectionHoistory || location.state?.inspectionHoistory;
  const [inspectiontask, setInspectiontask] =useState<any[]>([]);
  const compInspectionId = inspectionData ? inspectionData.compinspection_id : null;
  const token = localStorage.getItem("token");
  let selectedVenueId: string | number | null = null;
  try {
    selectedVenueId = token ? (jwtDecode(token) as any)?.cst || null : null;
  } catch {
    selectedVenueId = null;
  }
  const [openModal, setOpenModal] = useState(false);
  const [isAddItemModalOpen, setIsAddItemModalOpen] = useState(false);
  const [selectedInspectionItem, setSelectedInspectionItem] = useState<any | null>(null);
  const [inspectionStartedError, setInspectionStartedError] = useState("");

  const fetchTasks = async () => {
    if (!compInspectionId) return;
    try {
      const res = await fetch(`${apiUrl}/inspection_history/items/${compInspectionId}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          token: token || "",
        }
      });

      if (!res.ok) {
        console.error("Fetch error:", res.status, res.statusText);
        return;
      }
      const result = await res.json();
      if (result.success) {
        setInspectiontask(result.inspectionItems);
      } else {
        setInspectiontask([]);
      }
    } catch (err) {
      console.error("Fetch failed:", err);
    }
  };

  useEffect(() => {
    fetchTasks();
  }, [compInspectionId, token]);

  const {
    satisfactoryPercent,
    unsatisfactoryPercent,
  } = useMemo(() => {
    if (!inspectiontask || inspectiontask.length === 0) {
      return {
        satisfactoryPercent: 0,
        unsatisfactoryPercent: 0,
      };
    }

    const total = inspectiontask.length;
    const satisfactoryCount = inspectiontask.filter(i => i.status === 1).length;
    const unsatisfactoryCount = inspectiontask.filter(i => i.status === 2).length;

    return {
      satisfactoryPercent: ((satisfactoryCount / total) * 100).toFixed(2),
      unsatisfactoryPercent: ((unsatisfactoryCount / total) * 100).toFixed(2),
    };
  }, [inspectiontask]);


  const formatDateTime = (dateString: string) => {
    if (!dateString) return "";
    if (dateString.startsWith("1900-01-01")) return "";

    // Remove the Z so it's not interpreted as UTC
    const localDateString = dateString.replace(/Z$/, '');
    let date: Date;
    // Parse YYYY-MM-DD as local date to avoid timezone day shift
    if (/^\d{4}-\d{2}-\d{2}$/.test(localDateString)) {
      const [year, month, day] = localDateString.split("-").map(Number);
      date = new Date(year, month - 1, day);
    } else {
      date = new Date(localDateString);
    }
    if (Number.isNaN(date.getTime())) return "";

    const datePart = date.toLocaleDateString('en-AU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });

    return `${datePart}`;
  };

  const [formData, setFormData] = useState({
    inspectionType: "",
    inspector: "",
    inspectionDate: "",
    signOffDate: "",
    inspectionStarted: "",
    inspectionToBeCompletedBy: "",
    venue: "",
    venueArea: "",
    autoRenewal: "",
    renewPeriod: "",
    status: "",
    ciNumber: "",
    kpi: "",
    outcomeperc: "",
  });

  useEffect(() => {
    if (inspectionData) {
      // Auto Renewal: 1 => "Yes", 0 => "No"
      const autoRenewStr = inspectionData.autorenew === 1 ? "Yes" : "No";

      // Map periodtype number to string
      let renewPeriodStr = "";
      switch (inspectionData.periodtype) {
        case 1: renewPeriodStr = "Week"; break;
        case 2: renewPeriodStr = "2 Weeks"; break;
        case 3: renewPeriodStr = "Month"; break;
        case 4: renewPeriodStr = "3 Months"; break;
        case 5: renewPeriodStr = "6 Months"; break;
        case 6: renewPeriodStr = "Year"; break;
        default: renewPeriodStr = "";
      }

      const inspectionStartedYmd = parseDateToYmd(inspectionData.inspstart || "");
      const dueDateYmd = inspectionStartedYmd ? addDaysToYmd(inspectionStartedYmd, 5) : "";

      setFormData({
        inspectionType: inspectionData.inspectionType || "",
        inspector: inspectionData.inspector || "",
        inspectionDate: inspectionData.inspdate || "",
        signOffDate: inspectionData.signoffdate || "",
        inspectionStarted: inspectionStartedYmd,
        inspectionToBeCompletedBy: dueDateYmd,
        venue: inspectionData.venue || "",
        venueArea: inspectionData.area || "",
        autoRenewal: autoRenewStr,
        renewPeriod: renewPeriodStr,
        status: inspectionData.status || "",
        ciNumber: inspectionData.inspnumber || "",
        kpi: inspectionData.kpi || "",
        outcomeperc: inspectionData.outcomeperc || "",
      });

      setInspectionDate(inspectionData.inspstart || "");
    }
  }, [inspectionData]);

  const formatDateAsLocalYmd = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const parseDateToYmd = (value?: string) => {
    if (!value) return "";
    if (value.startsWith("1900-01-01")) return "";
    const raw = value.split("T")[0];
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
    const date = new Date(value.replace(/Z$/, ""));
    if (Number.isNaN(date.getTime())) return "";
    return formatDateAsLocalYmd(date);
  };

  const addDaysToYmd = (ymd: string, daysToAdd: number) => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return "";
    const [year, month, day] = ymd.split("-").map(Number);
    const date = new Date(year, month - 1, day);
    date.setDate(date.getDate() + daysToAdd);
    return formatDateAsLocalYmd(date);
  };

  const handleDateChange = (date: Date[]) => {
    if (date.length > 0) {
      const formattedDate = formatDateAsLocalYmd(date[0]);
      const dueDate = addDaysToYmd(formattedDate, 5);
      setFormData(prev => ({
        ...prev,
        inspectionStarted: formattedDate,
        inspectionToBeCompletedBy: dueDate,
      }));
      const inspectionDateYmd = parseDateToYmd(formData.inspectionDate);
      const todayYmd = formatDateAsLocalYmd(new Date());
      if (inspectionDateYmd && formattedDate < inspectionDateYmd) {
        setInspectionStartedError("Inspection Started Date CANNOT Be Before Inspection Date.");
        return;
      }
      if (formattedDate > todayYmd) {
        setInspectionStartedError("Inspection Started Date CANNOT Be Future Date.");
        return;
      }
      setInspectionStartedError("");
    } 
  };

 //Handle text input changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) {
      alert("Missing authorization token.");
      return;
    }

    if (!compInspectionId) {
      alert("Missing compinspection_id.");
      return;
    }

    const inspectionStartedValidationError = getInspectionStartedValidationError();
    if (inspectionStartedValidationError) {
      setInspectionStartedError(inspectionStartedValidationError);
      return;
    }
    setInspectionStartedError("");

    const inspstart = formData.inspectionStarted
      ? formData.inspectionStarted.split("T")[0]
      : "";

    const actionsDueDate = addDaysToYmd(inspstart, 5);

    try {
      setIsSubmitting(true);
      const response = await fetch(`${apiUrl}/inspection_history`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          token,
        },
        body: JSON.stringify({
          compinspection_id: compInspectionId,
          inspstart,
          actions_due_date: actionsDueDate,
          remarks: remarksInput.trim(),
          signoff: false,
        }),
      });

      const result = await response.json();
      if (response.ok && result.success) {
        navigate("/inspections-history");
        return;
      }

      alert(result?.message || "Failed to update inspection history");
    } catch (error) {
      console.error("Error updating inspection history:", error);
      alert("Failed to update inspection history");
    } finally {
      setIsSubmitting(false);
    }
  };

  const hasInspectionStartedDate = () => {
    const value = formData.inspectionStarted?.trim();
    return Boolean(value) && !value.startsWith("1900-01-01");
  };

  const getInspectionStartedValidationError = () => {
    const inspectionStarted = formData.inspectionStarted?.trim() || "";
    if (!inspectionStarted || inspectionStarted.startsWith("1900-01-01")) {
      return "Inspection Started is required.";
    }

    const inspectionDateYmd = parseDateToYmd(formData.inspectionDate);
    if (inspectionDateYmd && inspectionStarted < inspectionDateYmd) {
      return "Inspection Started Date CANNOT Be Before Inspection Date.";
    }

    const todayYmd = formatDateAsLocalYmd(new Date());
    if (inspectionStarted > todayYmd) {
      return "Inspection Started Date CANNOT Be Future Date.";
    }

    return "";
  };

  const inspectionRemarksText =
  inspectiontask.find(item => item.remarks)?.remarks || "";

  return(
    <>
      <ComponentCard title="Add new product">
        <Form onSubmit={handleSubmit} >
          <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
            <div className="space-y-6">
              <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <div className="col-span-2 sm:col-span-1">
                    <Label htmlFor="firstName">% Inspection Items With An Outcome</Label>
                    <Input type="text" value={formData.outcomeperc} onChange={handleChange} />
                  </div>
                  <div className="col-span-2 sm:col-span-1">
                    <Label htmlFor="lastName">Satisfactory %</Label>
                     <Input type="text" value={satisfactoryPercent} onChange={handleChange} />
                  </div>
                  <div className="col-span-2 sm:col-span-1">
                    <Label htmlFor="firstName">Unsatisfactory %</Label>
                    <Input type="text" value={unsatisfactoryPercent} onChange={handleChange} />
                  </div>
                  <div className="col-span-2 sm:col-span-1">
                    <Label htmlFor="lastName">KPI</Label>
                    <Input id="ciNumber" value={formData.kpi} onChange={handleChange} />
                  </div>
                </div>
              </div>
              <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
                <div className="flex flex-row-reverse gap-3 sm:flex-row sm:items-center">
                  <Button
                    className="px-2 py-1 mb-2 text-xs font-light rounded-lg bg-[#142900] hover:bg-[#3d5e42]" size="sm" 
                    type="button"
                    onClick={() => setIsAddItemModalOpen(true)}
                  >
                    Add Inspection Item
                  </Button>
                </div>
                <Table>
                  <TableHeader className="bg-[#d9ffa3]">
                    <TableRow>
                      <TableCell isHeader className=" px-2 py-2 border border-gray-100 dark:border-white/[0.05]">
                        <div className="flex items-center justify-between cursor-pointer">
                          <div className="flex gap-3">
                            <span className="font-semibold text-gray-700 text-theme-xs dark:text-gray-400">
                              Inspection Item
                            </span>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell isHeader className=" px-2 py-2 border border-gray-100 dark:border-white/[0.05]">
                        <div className="flex items-center justify-between cursor-pointer">
                          <div className="flex gap-3">
                            <span className="font-semibold text-gray-700 text-theme-xs dark:text-gray-400">
                              Heading
                            </span>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell isHeader className=" px-2 py-2 border border-gray-100 dark:border-white/[0.05]">
                        <div className="flex items-center justify-between cursor-pointer">
                          <div className="flex gap-3">
                            <span className="font-semibold text-gray-700 text-theme-xs dark:text-gray-400">
                              Outcome
                            </span>
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {inspectiontask.map((inspectionType) => (
                    <TableRow className="cursor-pointer hover:bg-[#ffffa3]" key={inspectionType.inspectiontask_id} 
                      onClick={() => {
                        if (!hasInspectionStartedDate()) {
                          setInspectionStartedError("Inspection Started is required.");
                          return;
                        }
                        const inspectionStartedValidationError = getInspectionStartedValidationError();
                        if (inspectionStartedValidationError) {
                          setInspectionStartedError(inspectionStartedValidationError);
                          return;
                        }
                        setInspectionStartedError("");
                        setSelectedInspectionItem(inspectionType);
                        setOpenModal(true);
                      }}
                    >
                      <TableCell className="px-4 py-4 font-normal text-gray-800 border border-gray-100 dark:border-white/[0.05] text-xs dark:text-gray-400 whitespace-normal">
                      <span>{inspectionType.task_name}</span>
                      </TableCell>
                      <TableCell className="px-4 py-4 font-normal text-gray-800 border border-gray-100 dark:border-white/[0.05] text-xs dark:text-gray-400 whitespace-normal">
                        <span>{inspectionType.heading_name}</span>
                      </TableCell>
                      <TableCell className="px-4 py-4 font-normal text-gray-800 border border-gray-100 dark:border-white/[0.05] text-xs dark:text-gray-400 whitespace-normal">
                        <span>
                          {inspectionType.status === 1
                            ? "Satisfactory"
                            : inspectionType.status === 2
                            ? "Unsatisfactory"
                            : ""}
                        </span>
                      </TableCell>
                    </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
                <div>
                  <Label>Inspection Remarks</Label>
                  <textarea
                    className={"w-full rounded-lg border px-4 py-2.5 text-sm shadow-theme-xs focus:outline-none text-gray-600 border-gray-300 focus:border-brand-300 focus:ring focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:focus:border-brand-800"}
                    rows={2}
                    value={remarksInput}
                    onChange={(e) => setRemarksInput(e.target.value)}
                    placeholder="Add new remark..."
                  />
                </div>
                <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
                  <Label>Inspection Remarks</Label>
                  <textarea
                    className="w-full rounded-lg border px-4 py-2.5 text-sm shadow-theme-xs
                      focus:outline-none text-gray-600 border-gray-300
                      dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
                    rows={6}
                    value={inspectionRemarksText}
                    readOnly
                  />
                </div>
              </div>
            </div>
            <div className="space-y-6">
              <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
                <div className="grid gap-6 sm:grid-cols-2">
                  <div className="col-span-full">
                    <h4 className="pb-4 text-base font-medium text-gray-800 border-b border-gray-200 dark:border-gray-800 dark:text-white/90">
                      Inspection Details
                    </h4>
                  </div>
                  <div className="">
                    <Label htmlFor="firstName">Inspection Type</Label>
                    <Input type="text"
                      id="inspectionType"
                      value={formData.inspectionType}
                      onChange={handleChange} 
                    />
                  </div>
                  <div className="">
                    <Label htmlFor="lastName">Inspector</Label>
                    <Input id="inspector" value={formData.inspector} onChange={handleChange} />
                  </div>
                  <div className="">
                    <Label htmlFor="firstName">Inspection Date</Label>
                    <Input id="inspectionDate" value={formatDateTime(formData.inspectionDate)} onChange={handleChange} />
                    
                  </div>
                  <div className="">
                    <Label htmlFor="lastName">Sign Off Date</Label>
                    <Input id="SignOffDate" value={formatDateTime(formData.signOffDate)} onChange={handleChange}/>
                  </div>
                  <div className="">
                    <Label htmlFor="inspectionStarted">Inspection Started</Label>
                    <div className="relative flatpickr-wrapper w-fit">
                      <Flatpickr
                        value={
                          formData.inspectionStarted &&
                          !formData.inspectionStarted.startsWith("1900-01-01")
                            ? formData.inspectionStarted
                            : undefined
                        }
                        onChange={handleDateChange}
                        options={{
                          dateFormat: "Y-m-d",
                        }}
                        className="w-full py-2 pl-3 pr-10 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                      />
                      <span className="absolute text-gray-500 -translate-y-1/2 pointer-events-none right-3 top-1/2 dark:text-gray-400">
                        <CalenderIcon />
                      </span>
                    </div>
                    {inspectionStartedError && (
                      <p className="mt-1 text-xs text-error-600">{inspectionStartedError}</p>
                    )}
                    </div>
                  <div className="">
                    <Label htmlFor="lastName">Inspection To Be completed By</Label>
                    <Input id="inspectionToBeCompletedBy" value={formatDateTime(formData.inspectionToBeCompletedBy)} onChange={handleChange} />
                  </div>
                  <div className="">
                    <Label htmlFor="firstName">Venue</Label>
                    <Input id="venue" value={formData.venue} onChange={handleChange}  />
                  </div>
                  <div className="">
                    <Label htmlFor="lastName">Venue Area</Label>
                    <Input id="venueArea" value={formData.venueArea} onChange={handleChange} />
                  </div>
                  <div className="">
                    <Label htmlFor="firstName">Auto Renewal</Label>
                    <Input id="autoRenewal" value={formData.autoRenewal} onChange={handleChange}/>
                  </div>
                  <div className="">
                    <Label htmlFor="lastName">Renew Period</Label>
                    <Input id="renewPeriod" value={formData.renewPeriod} onChange={handleChange} />
                  </div>
                  <div className="">
                    <Label htmlFor="firstName">Status</Label>
                    <Input id="status" value={formData.status} onChange={handleChange} />
                  </div>
                  <div className="">
                    <Label htmlFor="lastName">CI Number</Label>
                    <Input id="ciNumber" value={formData.ciNumber} onChange={handleChange} />
                  </div>
                </div>
              </div>
              <div className="flex gap-3">
                <Button size="sm" type="submit" disabled={isSubmitting}>
                  Update
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  type="button"
                  onClick={() => navigate("/inspections-history")}
                >
                  Cancel
                </Button>
                <button className="px-4 py-3 text-sm font-medium text-white rounded-lg bg-error-800 shadow-theme-xs hover:bg-error-600">
                  Delete
                </button>
              </div>
            </div>
          </div>
        </Form>
      </ComponentCard>
      <InspectionItemOutcome 
        isOpen={openModal}
        onClose={() => setOpenModal(false)}
        onSaved={fetchTasks}
        cst={selectedVenueId ?? inspectionData?.cst}
        compInspectionId={compInspectionId}
        inspectionId={inspectionData?.inspection_id ?? inspectionData?.inspectionid}
        inspectionItem={selectedInspectionItem}
      />
      <AddInspectionItemModal
        isOpen={isAddItemModalOpen}
        onClose={() => setIsAddItemModalOpen(false)}
        selectedInspectionId={compInspectionId}
        selectedVenueId={selectedVenueId}
        onSaved={fetchTasks}
      />
    </>
  )
}
